import { Marker, OverlayView } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import { useMapContext } from '@hero/web-context';
import { useMapMarkerColors } from '@hero/web-hooks';
import { MarkerCard } from './MarkerCard';

/**
 * PendingLocationMarker - Orange draggable marker shown when editing a single location
 * Uses MapContext as the single source of truth for editing location and handlers
 */
export function PendingLocationMarker() {
  const ctx = useMapContext();
  const { t } = useTranslation();

  // Early return if not editing or no editing location
  if (ctx.editingIndex === null || !ctx.editingLocation) {
    return null;
  }

  const location = ctx.editingLocation;
  const { markerColors } = useMapMarkerColors();
  
  const fillColor = markerColors.pending.fillColor;
  const pulseColor = markerColors.pending.paluse;

  const handleDragEnd = (e: google.maps.MapMouseEvent) => {
    if (e.latLng && ctx.handlePendingMarkerDrag) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      if (typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng)) {
        ctx.handlePendingMarkerDrag(lat, lng);
      }
    }
  };

  const iconUrl =
    'data:image/svg+xml;charset=UTF-8,' +
    encodeURIComponent(`
    <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <!-- Shadow matching Tailwind shadow-lg -->
        <filter id="markerShadow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="4"/>
          <feOffset dx="0" dy="5" result="offsetblur"/>
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.1"/>
          </feComponentTransfer>
          <feGaussianBlur stdDeviation="2"/>
          <feOffset dx="0" dy="2" result="offsetblur2"/>
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.1"/>
          </feComponentTransfer>
          <feMerge>
            <feMergeNode/>
            <feMergeNode/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      <!-- Outer pulse circle (animated) - 40px circle -->
      <circle cx="20" cy="20" r="20" fill="${pulseColor}" opacity="0.2">
        <animate attributeName="r" values="20;24;20" dur="2s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.2;0;0.2" dur="2s" repeatCount="indefinite"/>
      </circle>
      <!-- Main marker circle - 24px circle (12px radius), background color, white border-2, and shadow-lg -->
      <g filter="url(#markerShadow)">
        <circle cx="20" cy="20" r="12" fill="${fillColor}" stroke="#ffffff" stroke-width="2"/>
        <!-- Location pin icon - centered, white color -->
        <g transform="translate(20, 20)">
          <path d="M0 -7 C-2.5 -7 -4 -4.5 -4 -2 C-4 0.5 0 5 0 5 C0 5 4 0.5 4 -2 C4 -4.5 2.5 -7 0 -7 Z" fill="#ffffff"/>
          <circle cx="0" cy="-2" r="2" fill="${fillColor}"/>
        </g>
      </g>
    </svg>
  `);

  return (
    <>
      <Marker
        position={location.cords}
        draggable={true}
        clickable={false}
        icon={{
          url: iconUrl,
          scaledSize: new google.maps.Size(40, 40),
          anchor: new google.maps.Point(20, 20),
        }}
        onDragEnd={handleDragEnd}
        title={location.fullAddress || 'Selecting location...'}
      />
      {/* Tooltip overlay for address */}
      <OverlayView position={location.cords} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
        <div className="absolute bottom-[50px] left-1/2 -translate-x-1/2 min-w-[180px] z-10 pointer-events-none" dir="ltr">
          <MarkerCard
            location={location}
            index={ctx.editingIndex}
            isOrigin={ctx.editingIndex === 0}
            isEditing={true}
            title={t('markers.pendingLocation', 'Selecting Location')}
          />
          <div className="w-3 h-3 bg-white rotate-45 absolute -bottom-1.5 left-1/2 -translate-x-1/2 shadow-lg" />
        </div>
      </OverlayView>
    </>
  );
}
